import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Input, TextArea, Select, Switch, toast } from '../../components/common';
import { catalogApi } from '../../services/api';

export default function CatalogItemForm({ item, categories, onSuccess, onCancel }) {
    const isEditing = !!item;

    const [form, setForm] = useState({
        itemCode: '',
        name: '',
        description: '',
        categoryId: '',
        unitOfMeasureId: '',
        unitPrice: '',
        isActive: true
    });
    const [errors, setErrors] = useState({});

    // Fetch UOMs
    const { data: uomData } = useQuery({
        queryKey: ['uom'],
        queryFn: () => catalogApi.listUOM(),
        select: (res) => res.data
    });

    useEffect(() => {
        if (item) {
            setForm({
                itemCode: item.itemCode || '',
                name: item.name || '',
                description: item.description || '',
                categoryId: item.categoryId || '',
                unitOfMeasureId: item.unitOfMeasureId || '',
                unitPrice: item.unitPrice?.toString() || '',
                isActive: item.isActive ?? true
            });
        }
    }, [item]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? catalogApi.updateItem(item.id, data)
            : catalogApi.createItem(data),
        onSuccess: () => {
            toast.success(`Item ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save item');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.itemCode) newErrors.itemCode = 'Item code is required';
        if (!form.name) newErrors.name = 'Name is required';
        if (!form.categoryId) newErrors.categoryId = 'Category is required';
        if (!form.unitOfMeasureId) newErrors.unitOfMeasureId = 'UOM is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        mutation.mutate({
            ...form,
            categoryId: form.categoryId,
            unitOfMeasureId: form.unitOfMeasureId,
            unitPrice: parseFloat(form.unitPrice) || 0
        });
    };

    const categoryOptions = categories.map(c => ({ value: c.id.toString(), label: c.name }));
    const uomOptions = (uomData?.data || []).map(u => ({ value: u.id.toString(), label: `${u.name} (${u.code})` }));

    return (
        <form onSubmit={handleSubmit} className="item-form">
            <div className="form-grid">
                <Input
                    name="itemCode"
                    label="Item Code"
                    value={form.itemCode}
                    onChange={handleChange}
                    error={errors.itemCode}
                    disabled={isEditing}
                    required
                />
                <Input
                    name="name"
                    label="Item Name"
                    value={form.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                />
                <Select
                    name="categoryId"
                    label="Category"
                    value={form.categoryId}
                    onChange={handleChange}
                    options={categoryOptions}
                    error={errors.categoryId}
                    required
                />
                <Select
                    name="unitOfMeasureId"
                    label="Unit of Measure"
                    value={form.unitOfMeasureId}
                    onChange={handleChange}
                    options={uomOptions}
                    error={errors.unitOfMeasureId}
                    required
                />
                <Input
                    name="unitPrice"
                    type="number"
                    label="Unit Price"
                    value={form.unitPrice}
                    onChange={handleChange}
                />
                <div className="form-switch-wrapper">
                    <Switch
                        label="Active"
                        checked={form.isActive}
                        onChange={(checked) => setForm(prev => ({ ...prev, isActive: checked }))}
                    />
                </div>
            </div>

            <TextArea
                name="description"
                label="Description"
                value={form.description}
                onChange={handleChange}
                rows={3}
            />

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    {isEditing ? 'Update Item' : 'Create Item'}
                </Button>
            </div>
        </form>
    );
}

CatalogItemForm.propTypes = {
    item: PropTypes.object,
    categories: PropTypes.array,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
